/**
 * 第五階段（中正套件）：自由廣場／中正紀念堂和北投溫泉。
 * 標示「英文練習」的句子在所有語言中都刻意保留英文。
 */
import type { Messages } from './en';

export const zhTWCks: Pick<Messages, 'cks'> = {
  cks: {
    mapNames: { cks: '自由廣場', beitou: '北投溫泉' },
    zones: {
      ck_hall: '中正紀念堂', ck_gate: '自由廣場牌樓', ck_theater: '國家戲劇院', ck_concert: '國家音樂廳',
      ck_guanghua: '光華池', ck_yunhan: '雲漢池', ck_square: '自由廣場', ck_garden: '紀念堂花園',
      ck_valley: '地熱谷', ck_millennium: '千禧湯', ck_plumGarden: '梅庭', ck_library: '北投圖書館',
      ck_museum: '北投溫泉博物館', ck_btStation: '新北投站', ck_hotels: '溫泉旅館', ck_park: '北投公園',
    },
    objects: {
      ck_mrt: '捷運：中正紀念堂站', ck_gate: '拍照點：自由廣場牌樓', ck_steps: '89級階梯', ck_guard: '儀隊交接',
      ck_hall: '紀念堂匾額', ck_theater: '國家戲劇院', ck_concert: '國家音樂廳', ck_koi: '光華池的錦鯉',
      ck_yunhan: '雲漢池', ck_taichi: '晨間太極拳', ck_kite: '賣風箏的攤位', ck_snack: '雞蛋糕推車', ck_bubble: '珍珠奶茶攤',
      ck_corridor: '迴廊布告欄', ck_dance: '傍晚的舞蹈團', ck_pigeons: '鴿子',
      ck_btMrt: '捷運：新北投站', ck_oldStation: '新北投車站（古蹟）', ck_library: '臺北市立圖書館北投分館',
      ck_museum: '北投溫泉博物館', ck_bath: '千禧湯', ck_footbath: '免費泡腳池', ck_valley: '地熱谷',
      ck_eggs: '溫泉蛋', ck_plum: '梅庭', ck_stream: '北投溪',
    },
    npc: {
      ck_guard: '儀隊隊員', ck_guide: '導遊', ck_taichi: '太極拳師父', ck_taichi2: '打太極的奶奶', ck_kiteUncle: '風箏伯伯',
      ck_dancer: '舞蹈團領隊', ck_teacher: '校外教學的老師', ck_tourist: '背包客', ck_photographer: '攝影師',
      ck_koiGrandma: '餵魚奶奶', ck_student: '大學生', ck_volunteer: '導覽志工',
      ck_librarian: '圖書館員', ck_attendant: '浴池服務員', ck_eggAuntie: '溫泉蛋阿姨', ck_hikerA: '登山客', ck_hikerB: '登山爺爺',
      ck_footGrandpa: '林爺爺', ck_museumGuide: '博物館導覽員', ck_stationVol: '車站志工', ck_tourist2: '日本來的遊客', ck_kid: '小安',
    },
    items: {} as Record<string, { name: string; desc: string }>,

    // --- 共用 ------------------------------------------------------------------------------------
    listen: '🔊 再聽一次',
    next: '▶ 下一個',
    ok: '好！',
    notNow: '現在不要',
    learned: '你學到新東西了！⭐',
    poor: '你的金幣不夠。',

    // --- 自由廣場 ----------------------------------------------------------------------------------
    welcomeCks: '🏯 自由廣場到了！最遠那頭、藍色屋頂的白色建築就是紀念堂，要爬89級階梯才上得去。',
    gate: {
      text: '自由廣場牌樓高30公尺、寬80公尺，有五個門洞和藍色的琉璃瓦屋頂。上面的大字「自由廣場」，英文就是“Liberty Square”。',
      photo: '📸 請別人幫你拍照',
      how: '要怎麼說呢？（英文練習）',
      ask: ['Excuse me, could you take a photo of me, please?', 'Hey. Photo. Me. Now.', 'You take picture, I stand.'], // English practice
      wrong: '遊客猶豫了一下：“Er… could you ask more politely?”',
      done: '“Say cheese!” 📸 喀嚓！一張背景是牌樓的完美照片。',
    },
    steps: {
      text: '通往紀念堂的階梯共有89級——代表蔣中正的89歲。你能一口氣爬完嗎？',
      climb: '🧗 爬上89級階梯',
      quiz: '❓ 小測驗',
      counting: '“…eighty-seven, eighty-eight, eighty-nine!” 你爬到頂端了！整個廣場一直到牌樓都在你腳下。', // English practice
      countEn: 'Eighty-seven, eighty-eight, eighty-nine! I made it!', // English practice
      tired: '你太累了，爬不了89級階梯。先休息一下吧！',
      q: 'How many steps are there?', // English practice
      a: ['Eighty-nine', 'Forty-two', 'One hundred and one'], // English practice
      right: '答對了！89級階梯。數得真好！',
      wrong: '不太對喔。再數一次……一、二、三……',
    },
    guard: {
      notNow: (next: string) => `儀隊從早上9點到下午5點，每個整點交接一次。下一次是${next}。到時候再來吧！`,
      closed: '今天的交接儀式已經結束了。儀隊每天9:00到17:00每個整點交接。明天再來吧！',
      intro: '🎖️ 儀隊交接開始了！人群安靜下來。聽聽解說：',
      lines: [
        'The new guards march out slowly, step by step. Their boots click on the marble.', // English practice
        'Now they spin their rifles in the air — and they never drop them!', // English practice
        'The old guards and the new guards salute each other.', // English practice
        'The new guards stand completely still. They must not move, talk or smile for one hour!', // English practice
      ],
      done: '好精彩！儀隊要練好幾個月，才能動作整齊得像同一個人。',
      watch: '👀 觀看',
    },
    hall: {
      text: '國立中正紀念堂高70公尺，差不多有20層樓那麼高。它是用白色大理石蓋的，屋頂是藍色琉璃瓦。',
      q: 'How many sides does the roof have?', // English practice
      a: ['Eight', 'Six', 'Four'], // English practice
      right: '對！屋頂是八角形的：有八個邊，就像停車標誌一樣。',
      wrong: '再看看屋頂，數一數有幾個邊！',
    },
    theater: {
      text: '國家戲劇院看起來像一座古代宮殿，有金色的屋頂和紅色的柱子。這裡會演歌劇、芭蕾舞和舞台劇。',
      ask: '🎭 問問票的事',
      vendor: 'Hello! Can I help you?', // English practice
      ok: 'Two tickets for tonight’s show, please.', // English practice
      bad: ['Give ticket.', 'Show. Two. Me.'], // English practice
      done: '“Here you are: two tickets, row C. The show starts at seven thirty!”（你把票留下來當紀念。）', // English practice
    },
    concert: {
      text: '國家音樂廳是戲劇院的雙胞胎。管弦樂團、合唱團和鋼琴家都在這裡演出。',
      listen: '🎻 在門口聽一聽',
      heard: '從門縫裡聽到小提琴在練習。好好聽！🎶',
      phrase: 'What time does the concert start? — It starts at seven o’clock.', // English practice
    },
    koi: {
      text: '光華池裡滿滿都是錦鯉：橘色、白色、黑色和金色。有些錦鯉可以活超過50年！',
      feed: '🐟 買飼料餵錦鯉（5 枚金幣）',
      fed: '嘩啦！好幾十條錦鯉衝過來，嘴巴一張一合：「喔！喔！喔！」',
      en: 'Look! The koi are opening their mouths!', // English practice
      already: '錦鯉已經吃飽了。不要餵太多喔！',
    },
    yunhan: {
      text: '雲漢池的意思是「銀河池」。一座小橋橫跨池面，烏龜在石頭上曬太陽。',
      en: 'Look, there’s a turtle on the rock!', // English practice
      q: 'What animal is sunbathing on the rock?', // English practice
      a: ['A turtle', 'A cat', 'A pigeon'], // English practice
      right: '對，是烏龜！牠正在曬太陽取暖。',
      wrong: '再看仔細一點：牠有殼喔！',
    },
    taichi: {
      text: '每天早上，爺爺奶奶們都在這裡打太極拳。他們的動作很慢，像流水一樣。',
      join: '🧘 一起打',
      en: 'Breathe in… breathe out… move your arms slowly, like a cloud.', // English practice
      done: '你覺得心情平靜又有精神。身體暖暖的！',
      later: '太極拳隊每天早上6點到10點在這裡練習。早點來吧！',
      already: '你今天已經打過太極拳了。你的腿在說謝謝！',
    },
    kite: {
      text: '風箏伯伯賣各種彩色的風箏：龍、蝴蝶和魚。空曠的廣場上風很大。',
      buy: '🪁 買風箏來放（15 枚金幣）',
      en: 'The wind is strong today! Let out more string!', // English practice
      done: '你的風箏越飛越高，長長的尾巴在風中跳舞！',
      words: '新單字：kite · string · tail · wind', // English practice
    },
    snack: {
      vendor: 'Hi! Egg cakes, fresh and warm. What would you like?', // English practice
      ok: 'Can I have a bag of egg cakes, please?', // English practice
      bad: ['Egg cake. Give.', 'I want eat cake now.'], // English practice
      done: 'Here you go! Careful, they’re hot. Enjoy!', // English practice
    },
    bubble: {
      vendor: 'Hello! What can I get you?', // English practice
      ok: 'A medium bubble milk tea with less sugar, please.', // English practice
      bad: ['Tea! Big! Now!', 'Bubble give me.'], // English practice
      done: 'One medium bubble milk tea, less sugar. Here’s your straw!', // English practice
    },
    corridor: {
      intro: '藍色琉璃瓦迴廊裡的布告欄上，有給遊客的小測驗。',
      qs: [
        { q: 'What colour is the Memorial Hall’s roof?', a: ['Blue', 'Red', 'Green'] }, // English practice
        { q: 'Which building has a golden roof?', a: ['The National Theater', 'The MRT station', 'The gate'] }, // English practice
        { q: 'Where do the koi live?', a: ['In the ponds', 'In the trees', 'On the roof'] }, // English practice
      ],
      right: '答對了！✅',
      wrong: '嗯，不是這個。再試一次！',
      done: '你每一題都答完了！布告欄上寫著：“Thank you for visiting!”',
    },
    dance: {
      text: '每天傍晚，一群群跳舞的人會在戲劇院前面練舞，旁邊放著一台小音響。',
      join: '💃 一起跳舞',
      en: 'Step to the left, step to the right, and turn around!', // English practice
      done: '你跟大家一起跳完了！阿姨們都在為你拍手。',
      later: '舞蹈團傍晚6點到10點才會來。他們在為表演和比賽練習！',
    },
    pigeons: {
      text: '廣場上有好幾百隻鴿子走來走去。只要一受到驚嚇，牠們就會一起飛起來！',
      watch: '👀 看鴿子',
      en: 'Please don’t chase the pigeons!', // English practice
      done: '啪啪啪！一大群鴿子繞著廣場飛一圈，又降落下來。',
    },

    // --- 北投 ------------------------------------------------------------------------------------
    welcomeBeitou: '♨️ 歡迎來到北投！你聞到溫泉的味道了嗎？溪水上冒著熱氣。',
    oldStation: {
      text: '這座小小的木造車站建於1916年。1988年被搬走，2017年又搬回老家。現在是一座小博物館。',
      en: 'This station is over one hundred years old.', // English practice
      q: 'What is the old station made of?', // English practice
      a: ['Wood', 'Stone', 'Glass'], // English practice
      right: '對，是木頭！日式的木板牆和瓦片屋頂。',
      wrong: '看看牆壁：咖啡色的木板……',
    },
    library: {
      text: '北投圖書館是台灣第一座綠建築圖書館。它是木頭蓋的，屋頂有太陽能板，還會收集雨水來沖馬桶！',
      read: '📖 看一會兒書',
      readDone: '你在木頭陽台下看了一本關於火山的書。好安靜，好舒服。',
      en: 'Excuse me, where can I find books in English?', // English practice
      reply: '“They’re on the second floor, next to the window.”', // English practice
      ask: '🗣️ 問圖書館員',
      q: 'What do the solar panels on the roof make?', // English practice
      a: ['Electricity', 'Water', 'Books'], // English practice
      right: '答對了！陽光變成了電。',
      wrong: '想一想：太陽照在板子上，就會產生……',
      quiz: '❓ 小測驗',
    },
    museum: {
      text: '北投溫泉博物館建於1913年，原本是公共浴場——當時是東亞最大的。樓下（紅磚）是羅馬式的大浴池；樓上（木造）是可以休息的榻榻米房間。',
      en: 'Is the museum free? — Yes, it’s free!', // English practice
      q: 'What was the museum before?', // English practice
      a: ['A public bathhouse', 'A train station', 'A school'], // English practice
      right: '沒錯！一百多年前，大家會來這裡泡澡。',
      wrong: '提示：樓下有一個大浴池……',
    },
    bath: {
      text: '千禧湯是溪邊的露天公共溫泉。大家都要穿泳衣，池子從溫溫的到非常燙的都有。',
      go: '♨️ 泡一個小時（40 枚金幣）',
      rule: '服務員問：“What must you wear in the pools?”', // English practice
      a: ['A swimsuit', 'My school uniform', 'A raincoat'], // English practice
      wrong: '“No, no! Try again.”',
      done: '你先沖澡，再慢慢滑進溫暖的池子裡。啊……全身都放鬆了。😌',
      en: 'Please take a shower before you get in the pool.', // English practice
      already: '你今天已經泡過了。泡太久熱水會頭暈喔！',
      closed: '千禧湯現在沒開。早上5:30才開門。',
    },
    footbath: {
      text: '免費的公共泡腳池！坐在木頭池邊，把腳放進溫暖的水裡。',
      soak: '🦶 泡泡腳',
      phrases: [
        'The water is hot! Let me put my feet in slowly.', // English practice
        'It’s warm and cosy. I love it!', // English practice
        'Ouch, it’s too hot! Is it forty-two degrees?', // English practice
        'Ahh, my feet feel so good after walking all day.', // English practice
      ],
      done: '你的腳變得紅紅暖暖的。你覺得休息夠了！（+體力，+心情）',
      already: '你的腳已經皺得像葡萄乾了。一天泡一次就夠囉！',
      q: 'The water is 42°C. Is it hot or cold?', // English practice
      a: ['It’s hot', 'It’s cold', 'It’s frozen'], // English practice
      right: '對，很燙！Hot · warm · cool · cold.', // English practice
      wrong: '嗯，42度比你的體溫（37度）還要高……',
      quiz: '🌡️ 溫度小測驗',
    },
    valley: {
      text: '地熱谷：翠綠色的泉水冒著泡，溫度最高可達90°C！水蒸氣有硫磺味，聞起來像臭雞蛋。以前大家叫它「地獄谷」。',
      en: 'Danger! Do not touch the water. It’s very hot!', // English practice
      q: 'How hot can the water in Thermal Valley get?', // English practice
      a: ['About ninety degrees', 'About twenty degrees', 'About zero degrees'], // English practice
      right: '對，幾乎快沸騰了！所以才會有欄杆。',
      wrong: '再看看水蒸氣和警告標誌……',
      fact: '這裡會長出一種很稀有的綠色礦物，叫做北投石。全世界只有兩個地方找得到！',
    },
    eggs: {
      vendor: 'Hot-spring eggs! Cooked in the hot water. How many would you like?', // English practice
      ok: 'Three eggs, please.', // English practice
      bad: ['Egg. Three. Now.', 'Give me egg you.'], // English practice
      done: 'Here you are, three warm eggs with a little salt. The yolk is soft and creamy!', // English practice
    },
    plum: {
      text: '梅庭是書法家于右任的避暑別墅。梅花是中華民國的國花。',
      en: 'The plum blossom is the national flower of Taiwan.', // English practice
      q: 'When do plum flowers bloom?', // English practice
      a: ['In winter', 'In summer', 'Never'], // English practice
      right: '對！梅花在寒冷的冬天開花，所以代表勇敢和堅強。',
      wrong: '提示：天氣冷的時候才開……',
    },
    stream: {
      text: '北投溪的水是溫的！溫泉水從地熱谷流下來，天氣涼的時候，溪面上會飄著熱氣。',
      en: 'What’s that smell? — It’s sulphur from the hot springs.', // English practice
    },

    // --- 人物 ------------------------------------------------------------------------------------
    talk: {
      ck_guard: [
        { text: '我現在休息中。在紀念堂裡，我們要一動也不動站一個小時。不能動，也不能笑！' },
        { text: '我們的槍大約四公斤重。每天練習都要轉槍。' },
        { text: '遊客問我：“How do you stand so still?” 我說：“Practice, practice, practice!”', en: 'Practice, practice, practice!' }, // English practice
      ],
      ck_guide: [
        { text: '大家跟著黃色旗子走！紀念堂往這邊。', en: 'Follow me, please! This way.' }, // English practice
        { text: '戲劇院和音樂廳隔著廣場面對面，就像一對雙胞胎。' },
        { text: '小提醒：晚上紀念堂打上白色燈光的時候很漂亮。' },
      ],
      ck_taichi: [
        { text: '早安！打太極讓我保持年輕。我八十二歲了！', en: 'Good morning! How are you today?' }, // English practice
        { text: '膝蓋稍微彎一點……慢慢推出去，像這樣。' },
      ],
      ck_taichi2: [
        { text: '我們每天早上六點都來，不管晴天雨天。', en: 'Rain or shine!' }, // English practice
        { text: '打完太極，我們會一起去吃豆漿和蛋餅。好好吃！' },
      ],
      ck_kiteUncle: [
        { text: '逆著風跑，再一點一點把線放出去！', en: 'Let the string out little by little.' }, // English practice
        { text: '我最厲害的風箏是一條十公尺長的龍。要三個人才放得起來！' },
      ],
      ck_dancer: [
        { text: '五、六、七、八！再從頭來一次！', en: 'Five, six, seven, eight!' }, // English practice
        { text: '我們在為下個月的比賽練習。要不要一起跳？' },
      ],
      ck_teacher: [
        { text: '同學們，大家不要走散！不要在階梯上跑！', en: 'Stay together, please!' }, // English practice
        { text: '今天我們要學台灣的歷史和建築。' },
      ],
      ck_tourist: [
        { text: '嗨！我是從加拿大來的。捷運站在哪裡？', en: 'Excuse me, where is the MRT station?' }, // English practice
        { text: '參觀紀念堂要錢嗎？——不用錢！謝謝！', en: 'Is it free to visit?' }, // English practice
        { text: '我好愛珍珠奶茶。英文叫“bubble tea”或“boba”喔！' }, // English practice
      ],
      ck_photographer: [
        { text: '從這裡拍最好看：整座牌樓都入鏡，後面還有藍天。' },
        { text: '笑一個，說“cheese”！', en: 'Smile and say cheese!' }, // English practice
      ],
      ck_koiGrandma: [
        { text: '那條大大的橘色錦鯉叫「福氣」。我已經餵牠十年了！' },
        { text: '錦鯉的顏色有橘色、白色、黑色、金色。你最喜歡哪一種？', en: 'Which colour is your favourite?' }, // English practice
      ],
      ck_student: [
        { text: '我在迴廊這裡讀書。這裡涼涼的、很安靜，還有微風。' },
        { text: '我在練英文準備考試。“How long have you lived in Taipei?”', en: 'How long have you lived in Taipei?' }, // English practice
      ],
      ck_volunteer: [
        { text: '歡迎！紀念堂早上九點開門，晚上六點關門。', en: 'The hall opens at nine and closes at six.' }, // English practice
        { text: '藍色的琉璃瓦和白色大理石，讓這座建築看起來又寧靜又雄偉。' },
      ],
      ck_librarian: [
        { text: '噓！這裡是圖書館，請小聲說話。', en: 'Please speak quietly.' }, // English practice
        { text: '大大的木頭陽台可以擋住陽光，所以冷氣可以少開一點。' },
      ],
      ck_attendant: [
        { text: '先沖澡、穿上泳衣，毛巾不可以放進水裡！', en: 'Please shower first.' }, // English practice
        { text: '最燙的池子在最上面，大約四十四度。要小心喔！' },
      ],
      ck_eggAuntie: [
        { text: '我的蛋是用溫泉水煮的。吃得到礦物質的味道喔！' },
        { text: '要幾顆？一、二、三……我會用英文數數喔！', en: 'How many eggs would you like?' }, // English practice
      ],
      ck_hikerA: [
        { text: '我從陽明山走下來的。山上有火山喔！' },
        { text: '去爬山別忘了帶水和帽子。', en: 'Don’t forget your water bottle!' }, // English practice
      ],
      ck_hikerB: [
        { text: '我七十歲了，每個週末還是去爬山！' },
        { text: '爬完山泡個腳，是最棒的獎勵。', en: 'What a beautiful day for a walk!' }, // English practice
      ],
      ck_footGrandpa: [
        { text: '來，坐！今天的水溫剛剛好，大約四十度。', en: 'The water is just right.' }, // English practice
        { text: '我小時候，這裡的溫泉到處都是熱氣和歌聲。' },
        { text: 'Hot、warm、cool、cold。跟我一起說！', en: 'Hot, warm, cool, cold.' }, // English practice
      ],
      ck_museumGuide: [
        { text: '上樓到榻榻米房間之前，要先脫鞋喔。', en: 'Please take off your shoes.' }, // English practice
        { text: '樓下的彩繪玻璃窗已經有一百多年了。' },
      ],
      ck_stationVol: [
        { text: '以前這座老車站有一條小火車支線，載泡湯的客人從北投到這裡。' },
        { text: '抬頭看看屋頂的窗戶。它讓光線照進候車室。' },
      ],
      ck_tourist2: [
        { text: 'Konnichiwa！北投感覺好像日本的溫泉小鎮。' },
        { text: '我每次都會吃溫泉蛋。真的好好吃！', en: 'They are so good!' }, // English practice
      ],
      ck_kid: [
        { text: '溪水是溫的！媽媽說不可以把手放進去。' },
        { text: '為什麼聞起來像雞蛋？——是硫磺！我今天學到這個詞。', en: 'It smells like eggs!' }, // English practice
      ],
    } as Record<string, { text: string; en?: string }[]>,
  },
};
